import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon, SpinnerIcon } from '@tonic-one/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';

import highSvg from '../../../assets/image/high-protection.svg';
import lowSvg from '../../../assets/image/low-protection.svg';
import mediumSvg from '../../../assets/image/medium-protection.svg';
import naSvg from '../../../assets/image/na-protection.svg';
import DataTrack from '../../../components/DataTrack';
import TableTemplate from '../Charts/TableTemplate';
import { Report } from './report';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';

const Summary = (props: {
  report: Report | null;
  services: string[];
  onRefresh: () => void;
  refreshing: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  const protectionLevel = computeProtectionLevel(props.report, props.services);
  const protectionTable = computeProtectionTable(
    props.report,
    props.services
  ).map(([service, percent]) => [
    t(`common.service.${service}`),
    <Flex direction="row" align="center">
      <Flex
        width="68px"
        height="6px"
        mr="12px"
        background="#5E5E5E"
        direction="row"
        align="center"
      >
        <Box
          width={`${(68 / 100) * percent}px`}
          height="6px"
          background="#578AEF"
        />
      </Flex>
      {`${percent}%`}
    </Flex>,
  ]);

  return (
    <Box p="14px 14px 30px" color={colorStyle.color.primary}>
      <Flex
        direction="row"
        align="center"
        justify="flex-start"
        fontSize="18px"
        fontWeight="600"
      >
        {t('recommendConfig.filter.featureAdoptionStatus')}
        <Popover trigger="hover">
          <PopoverTrigger>
            <Icon as={InfoOIcon} marginLeft="8px" />
          </PopoverTrigger>
          <PopoverContent>
            <Box fontSize="14px" fontWeight="400" textTransform="none">
              {t('recommendConfig.health.desc1')}
              &nbsp;
              <a
                href={getCasOLHLink(
                  CAS_DOC_LINK.dashboard.protectionFeatureAdoption,
                  i18next.language
                )}
                target="_blank"
              >
                {t('recommendConfig.health.desc2')}
              </a>
              &nbsp;
              {t('recommendConfig.health.desc3')}
            </Box>
          </PopoverContent>
        </Popover>
      </Flex>
      <ProtectionGraph level={protectionLevel} />
      <Flex direction="row" justify="center" m="2px 0 40px">
        <DataTrack type="ConfigRecommendation.FeatureAdoption.Refresh.Click">
          <Button
            variant="secondary"
            disabled={props.refreshing}
            onClick={props.onRefresh}
            width="98px"
          >
            {props.refreshing ? <Icon as={SpinnerIcon} spin mr="10px" /> : null}
            {t('recommendConfig.filter.checkConfig')}
          </Button>
        </DataTrack>
      </Flex>
      <Box fontSize="18px" fontWeight="600">
        {t('recommendConfig.filter.featureAdoptionRate')}
      </Box>
      <TableTemplate
        config={[
          { width: '50%', align: 'left' },
          { width: '50%', align: 'left' },
        ]}
        header={[
          t('recommendConfig.filter.services'),
          t('recommendConfig.filter.adoptionRate'),
        ]}
        data={protectionTable}
      />
    </Box>
  );
};

function ProtectionGraph(props: { level: string | null }) {
  const { t } = useTranslation();

  let text = '';
  let img = '';
  if (props.level === 'high') {
    text = t('recommendConfig.filter.good');
    img = highSvg;
  } else if (props.level === 'medium') {
    text = t('recommendConfig.filter.partial');
    img = mediumSvg;
  } else if (props.level === 'low') {
    text = t('recommendConfig.filter.warning');
    img = lowSvg;
  } else {
    text = 'N/A';
    img = naSvg;
  }

  return (
    <Flex mt="35px" direction="column" align="center">
      <img src={img} />
      <Flex
        direction="row"
        align="flex-end"
        justify="center"
        position="relative"
        top="-25px"
        fontSize="16px"
        textAlign="center"
        lineHeight="1.3em"
      >
        {text}
      </Flex>
    </Flex>
  );
}

function computeProtectionTable(
  report: Report | null,
  services: string[]
): [string, number][] {
  return services.map((service) => {
    if (!report) {
      return [service, 0];
    }

    const svcReport = report[service];
    if (!svcReport) {
      return [service, 0];
    }

    let total = 0;
    let legal = 0;
    for (const threatReport of Object.values(svcReport)) {
      for (const item of threatReport) {
        total += 1;
        if (item.legal) {
          legal += 1;
        }
      }
    }

    if (total === 0) {
      return [service, 0];
    }

    return [service, Math.round((legal / total) * 100)];
  });
}

function computeProtectionLevel(report: Report | null, services: string[]) {
  if (!report || services.length === 0) {
    return null;
  }

  let protection = 0;

  for (const service of services) {
    const svcReport = report[service];
    if (!svcReport) {
      return 'low';
    }

    for (const threatReport of Object.values(svcReport)) {
      for (const item of threatReport) {
        if (!item.legal && item.protection > protection) {
          protection = item.protection;
        }
      }
    }
  }

  if (protection <= 10) {
    return 'high';
  } else if (protection <= 20) {
    return 'medium';
  } else {
    return 'low';
  }
}

export default Summary;
